/**
 * App API del módulo POS — hooks, use cases, stores, presentation y tipos que
 * dependen del store. Importar desde acá para evitar require cycles.
 */

export { usePosCatalogPage } from './application/usePosCatalogPage';
export type { PosProductRow } from './application/usePosCatalogPage';
export { isPosPackageRow } from './domain/posTypes';
export type {
  PosCatalogItemTypeFilter,
  PosCatalogRow,
  PosPackageGridRow,
} from './domain/posTypes';

export {
  CART_LINE_STOCK_HINT,
  buildStockTapNoticeCopy,
  formatCheckoutStockIssuesSummary,
  getStockWarningSeverity,
  mapStockWarningsByProductId,
} from './application/posCartStockIssuePresentation';

export {
  countPosCheckoutCustomers,
  listPosCheckoutCustomers,
  loadOpenCheckoutContext,
} from './application/posCheckoutCommands';

export type { CustomerCreditExceededRow, NormalizedPaymentDraft } from './application/posCheckoutValidation';
export {
  findCustomerCreditExceeded,
  normalizePaymentDrafts,
  pickDefaultPaymentMethodId,
  validateCheckoutDraftsBasic,
  validateCheckoutPaidVersusTotal,
} from './application/posCheckoutValidation';

export {
  POS_GRID_PHYSICAL_STOCK_ONLY_STORAGE_KEY,
  POS_MOBILE_CART_BAR_HEIGHT,
  POS_MOBILE_CART_COMPACT_LAYOUT,
  POS_MOBILE_CART_SHEET_LAYOUT,
} from './application/posLayout.constants';

export {
  derivePosCheckoutTotals,
  isProductCardOutOfStock,
  effectivePosSellableQuantity,
  formatSkuForPosCard,
} from './application/posScreen.utils';

export {
  computePosCheckoutTotals,
  toPaymentAmountNumber,
  type PosCheckoutLineTotalInput,
  type PosCheckoutPaymentAmountInput,
} from './application/posCheckoutTotals';

export {
  checkoutChangeAmount,
  derivePosCheckoutMoney,
  saleDetailChangeAmount,
} from './application/posCheckoutMoney';

export {
  getOrganizationBusinessProfileForPos,
  hasPosStaffTipsCapability,
  isPosStaffTipsEnabled,
  resolvePosStaffTipAmount,
  usePosStaffTipsEnabled,
} from './application/posStaffTips';

export {
  mapPosCartToPricedLines,
  evaluatePosCartPromotions,
  listCashierSelectablePromotions,
  validatePromoCodeForCart,
} from './application/posCartPromotions';

export { usePosCartPromotions } from './application/usePosCartPromotions';
export type { CartPromoUiStatus } from './application/posCartPromoPresentation';

// posParkedSaleTypes depende del store (CartItem), por eso vive en app-api y no en domain-api.
export type { PosCartPersistenceScope, PosCartPromoSnapshot } from './domain/posParkedSaleTypes';

export { usePosRemoteSaleDraft } from './application/usePosRemoteSaleDraft';
export { clearPosCartPersistenceForUser } from './infra/posLegacyDraftCleanup';

export { mapPosCreateSaleRequest } from './application/posCreateSaleRequest';

export type { PosNavigationIntent } from './application/posNavigationIntent';
export { setPosNavigationIntent, consumePosNavigationIntent } from './application/posNavigationIntent';

export { usePosStore } from './store/usePosStore';
export type { CartItem } from './store/usePosStore';
export { isFractionalCartLine } from './store/usePosStore';

export { usePosCashSessionStore } from './store/usePosCashSessionStore';
export { usePosOverlayStore } from './store/usePosOverlayStore';
export { usePosCheckoutStore } from './store/usePosCheckoutStore';

// Re-export desde sales para mantener la superficie pública de POS.
export { getStockWarnings as getPosStockWarningsForSale } from '../sales/infra/salesRepo';
